import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { Briefcase, Mail, Phone, MapPin, FileText, Edit2, Layers, Compass, Clock, Award } from "lucide-react";
import Badge from "@/components/common/Badge";
import Button from "@/components/common/Button";
import TextInput from "@/components/common/TextInput";
import Modal from "@/components/common/Modal";
import { updateSponsorEmail } from "@/store/slices/sponsorSlice";
import { toast } from "react-toastify";
import { getImageUrl } from "@/utils/imageUrl";

const SPONSORSHIP_TYPE_LABELS = {
  funded: "Funded",
  kind: "In-Kind",
  funded_and_kind: "Funded & In-Kind",
};

const DURATION_LABELS = {
  year: "Yearly",
  month: "Monthly",
  event: "Per Event",
};

const CATEGORY_LABELS = {
  hydration: "Hydration",
  medals: "Medals",
  nutrition: "Nutrition",
  apparels: "Apparels",
  gears: "Gears",
  spares: "Spares",
  accessories: "Accessories",
  registration_fees: "Registration Fees",
  insurance: "Insurance",
  bikes: "Bikes",
  certificates: "Certificates",
  route_signages: "Route Signages",
  branding: "Branding",
  finance_and_support_vehicles: "Finance & Support Vehicles",
};

const SettingsProfile = () => {
  const dispatch = useDispatch();
  const { sponsor, isLoading } = useSelector((state) => state.sponsor);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({
    new_email: "",
    current_password: ""
  });

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.new_email || !emailForm.current_password) {
      toast.warn("Please fill all fields");
      return;
    }

    try {
      await dispatch(updateSponsorEmail(emailForm)).unwrap();
      toast.success("Email updated successfully");
      setIsEditingEmail(false);
      setEmailForm({ new_email: "", current_password: "" });
    } catch (error) {
      toast.error(error.message || "Failed to update email");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 max-w-2xl"
    >
      <div className="flex items-center gap-4 mb-8">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center overflow-hidden shadow-lg">
          {sponsor?.logo_url ? (
            <img 
              src={getImageUrl(sponsor.logo_url)} 
              alt={sponsor.company_name} 
              className="w-full h-full object-cover" 
            />
          ) : (
            <Briefcase className="w-8 h-8 text-white" />
          )}
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
            {sponsor?.company_name || "Company"}
          </h2>
          <div className="flex items-center gap-2 mt-1">
            <Badge tone="sponsor" size="sm">
              Sponsor
            </Badge>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700/50">
          <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
            <div className="flex items-center gap-3">
              <p className="font-medium text-gray-800 dark:text-gray-100">
                {sponsor?.email || sponsor?.contact_email}
              </p>
              <button
                onClick={() => setIsEditingEmail(true)}
                className="p-1 text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                title="Change Email"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700/50">
          <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Phone</p>
            <p className="font-medium text-gray-800 dark:text-gray-100">
              {sponsor?.contact_phone || "Not provided"}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700/50">
          <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">GST Number</p>
            <p className="font-medium text-gray-800 dark:text-gray-100">
              {sponsor?.gst_number || "Not provided"}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700/50">
          <Layers className="w-5 h-5 text-gray-400 mt-0.5" />
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Industry Categories</p>
            <div className="flex flex-wrap gap-2 mt-1">
              {sponsor?.categories?.length > 0 ? (
                sponsor.categories.map((cat, index) => (
                  <Badge key={index} tone="info" size="sm">{CATEGORY_LABELS[cat] || cat}</Badge>
                ))
              ) : (
                <p className="font-medium text-gray-800 dark:text-gray-100">Not provided</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700/50">
          <Compass className="w-5 h-5 text-gray-400 mt-0.5" />
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Interested Zones</p>
            <div className="flex flex-wrap gap-2 mt-1">
              {sponsor?.zones?.length > 0 ? (
                sponsor.zones.map((zone, index) => (
                  <Badge key={index} tone="warning" size="sm">{zone}</Badge>
                ))
              ) : (
                <p className="font-medium text-gray-800 dark:text-gray-100">Not provided</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700/50">
          <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Duration of Interest</p>
            <p className="font-medium text-gray-800 dark:text-gray-100">
              {DURATION_LABELS[sponsor?.application_id?.duration_interest] || sponsor?.application_id?.duration_interest || "Not provided"}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700/50">
          <Award className="w-5 h-5 text-gray-400 mt-0.5" />
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Sponsorship Type</p>
            <p className="font-medium text-gray-800 dark:text-gray-100">
              {SPONSORSHIP_TYPE_LABELS[sponsor?.application_id?.sponsorship_type] || sponsor?.application_id?.sponsorship_type || "Not provided"}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl text-sm text-gray-500 dark:text-gray-400 border border-gray-100 dark:border-gray-700">
        To update other details, please contact koset support to prevent unauthorized changes.
      </div>

      {/* Email Update Modal */}
      <Modal
        open={isEditingEmail}
        onClose={() => setIsEditingEmail(false)}
        title="Update Email Address"
        showCloseButton={false}
      >
        <form onSubmit={handleUpdateEmail} className="space-y-4 mt-2">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Please enter your new email address and confirm with your current password.
          </p>
          
          <TextInput
            label="New Email Address"
            type="email"
            placeholder="Enter new email"
            value={emailForm.new_email}
            onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
            required
            icon={<Mail className="w-4 h-4" />}
          />
          
          <TextInput
            label="Current Password"
            type="password"
            placeholder="Confirm with password"
            value={emailForm.current_password}
            onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
            required
            icon={<Lock className="w-4 h-4" />}
          />

          <div className="flex justify-end gap-3 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsEditingEmail(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              disabled={isLoading}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              Update Email
            </Button>
          </div>
        </form>
      </Modal>
    </motion.div>
  );
};

export default SettingsProfile;
